/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Share2Icon } from '@/shared/ui/radix-icons';
import { agencyContext } from '@pages/authenticated/agency-pages/AgenciesContext';
import { useAgencyPublications } from '../hooks/use-agency-publications';
import { PublicationSections } from '../components/PublicationSections';
import { PublicationDetailModal } from '../components/PublicationDetailModal';
import type { CatalogPublication } from '../types';
import { hasListingPortalConnection, hasSocialPortalConnection } from '../has-social-portal-connection';
import { FF_APP_FACEBOOK, FF_APP_INSTAGRAM_NATIVE, FF_APP_PORTALS_INTEGRATION } from '@/shared/utils/feature-flag';
import {
  API_BASE,
  buildHeaders,
} from '@features/property-management/components/publications-center/api';
import { Anchor, ButtonLink, Text, Title } from '@/shared/ui';
import { adReadinessFrom, NOTHING_CONFIGURED, savedBudgetCents, savedDurationDays, type AdReadiness } from '@/shared/utils/ad-readiness';

/** Link to the agency's social-network settings section. */
const SETTINGS_NETWORKS_HREF = (agencyId: string) => `/app/agencies/${agencyId}/settings?section=facebook`;
/** Link to the agency's listing-portal settings section. */
const SETTINGS_PORTALS_HREF = (agencyId: string) => `/app/agencies/${agencyId}/settings?section=portais`;

/**
 * Page that lists an agency's publications on social networks and listing
 * portals (OLX, ...), grouped by portal, with detail, edit, and removal flows.
 *
 * @returns The publications catalog page element.
 */
export function PublicationsCatalogPage() {
  const { t } = useTranslation('@features/properties');
  const { agency_id } = useParams<{ agency_id: string }>();
  const agencyId = agency_id ?? agencyContext.useStore((s) => s.agency?.id) ?? '';

  const { groups, loading, error, removeEntry, refresh } = useAgencyPublications(agencyId);
  const [selected, setSelected] = useState<CatalogPublication | null>(null);
  const [fbHasPaymentMethod, setFbHasPaymentMethod] = useState(false);
  const [connectedPortals, setConnectedPortals] = useState<string[]>([]);
  const [settingsReady, setSettingsReady] = useState(false);
  // The agency's own saved amounts; 0 when it has saved none (no built-in amount).
  const [fbLeadAdBudget, setFbLeadAdBudget] = useState(0);
  const [fbLeadAdDuration, setFbLeadAdDuration] = useState(0);
  const [igLeadAdBudget, setIgLeadAdBudget] = useState(0);
  const [igLeadAdDuration, setIgLeadAdDuration] = useState(0);
  const [adReadiness, setAdReadiness] = useState<AdReadiness>(NOTHING_CONFIGURED);

  useEffect(() => {
    if (!agencyId) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = await buildHeaders();
        const res = await axios.get(
          `${API_BASE}/portal-integration/agencies/${agencyId}/settings`,
          { headers },
        );
        if (!cancelled) {
          setFbHasPaymentMethod(Boolean(res.data?.fb_has_payment_method));
          setConnectedPortals(
            Array.isArray(res.data?.connected_portals) ? res.data.connected_portals : [],
          );
          setFbLeadAdBudget(savedBudgetCents(res.data?.fb_lead_ad_budget));
          setFbLeadAdDuration(savedDurationDays(res.data?.fb_lead_ad_duration));
          setIgLeadAdBudget(savedBudgetCents(res.data?.ig_lead_ad_budget));
          setIgLeadAdDuration(savedDurationDays(res.data?.ig_lead_ad_duration));
          setAdReadiness(adReadinessFrom(res.data));
        }
      } catch {
        // leave defaults; still mark ready so empty-state CTA can appear
      } finally {
        if (!cancelled) setSettingsReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [agencyId]);

  const handleSelect = useCallback((entry: CatalogPublication) => {
    setSelected(entry);
  }, []);

  const handleClose = useCallback(() => {
    setSelected(null);
  }, []);

  const handleRemove = useCallback(
    async (entry: CatalogPublication) => {
      await removeEntry(entry.property_id, entry.portal, entry.publication_id);
    },
    [removeEntry],
  );

  // Connecting either kind of channel counts: an agency that only uses OLX is not "unconnected".
  const hasConnection = hasSocialPortalConnection(connectedPortals) || hasListingPortalConnection(connectedPortals);
  const showConnectCta = settingsReady && !hasConnection;
  const showConnectMoreCta = settingsReady && hasConnection;
  const canConnectNetworks = FF_APP_FACEBOOK || FF_APP_INSTAGRAM_NATIVE;
  const canConnectPortals = FF_APP_PORTALS_INTEGRATION;

  return (
    <div
      className="flex flex-col gap-(--space-8) px-(--space-4) py-(--space-6) overflow-y-auto h-full sm:px-(--space-6) lg:px-(--space-8)"
      style={{ background: 'var(--surface-secondary)' }}
    >
      <div data-tour="publications-header" className="flex items-center justify-between">
        <Title order={1}
          className="m-0 font-bold"
          style={{ fontSize: 'clamp(18px, 2vw, 24px)', color: 'var(--text-primary)' }}
        >
          {t('publications.catalog.title')}
        </Title>
        {showConnectMoreCta && (
          <div className="flex items-center gap-(--space-2)">
            {canConnectNetworks && (
              <ButtonLink
                variant="outlined"
                size="xs"
                href={SETTINGS_NETWORKS_HREF(agencyId)}
                data-testid="catalog-connect-more-networks-link"
              >
                {t('publications.catalog.connectMoreNetworks')}
              </ButtonLink>
            )}
            {canConnectPortals && (
              <ButtonLink
                variant="outlined"
                size="xs"
                href={SETTINGS_PORTALS_HREF(agencyId)}
                data-testid="catalog-connect-more-portals-link"
              >
                {t('publications.catalog.connectMorePortals')}
              </ButtonLink>
            )}
          </div>
        )}
      </div>

      {loading && (
        <div data-testid="catalog-loading" className="flex flex-col gap-(--space-6)">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex flex-col gap-(--space-3) animate-pulse">
              <div
                className="rounded"
                style={{ height: 18, width: 120, background: 'var(--surface-tertiary)' }}
              />
              <div className="flex gap-(--space-3)">
                {[1, 2, 3, 4].map((j) => (
                  <div
                    key={j}
                    className="rounded-(--radius-xl) shrink-0"
                    style={{ width: 180, height: 135, background: 'var(--surface-tertiary)' }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && error && (
        <div
          data-testid="catalog-error"
          className="flex flex-col items-center justify-center py-(--space-16) gap-(--space-2)"
        >
          <Text className="m-0 text-sm font-semibold" style={{ color: 'var(--status-error-text)' }}>
            {t('publications.catalog.loadError')}
          </Text>
          <Text className="m-0 text-xs" style={{ color: 'var(--text-tertiary)' }}>
            {error.message}
          </Text>
        </div>
      )}

      {!loading && !error && groups.length === 0 && (
        <div
          data-testid="catalog-empty"
          className="flex flex-col items-center justify-center py-(--space-24) gap-(--space-3)"
        >
          <Share2Icon
            width={40}
            height={40}
            aria-hidden="true"
            style={{ color: 'var(--text-tertiary)' }}
          />
          <Text
            className="m-0 text-sm font-semibold text-center"
            style={{ color: 'var(--text-secondary)' }}
          >
            {t('publications.catalog.emptyTitle')}
          </Text>
          <Text
            className="m-0 text-xs text-center"
            style={{ color: 'var(--text-tertiary)', maxWidth: 260 }}
          >
            {t('publications.catalog.emptyHint')}
          </Text>
          {showConnectCta && (
            <div className="flex items-center gap-(--space-2) flex-wrap justify-center">
              {canConnectNetworks && (
                <Anchor
                  href={SETTINGS_NETWORKS_HREF(agencyId)}
                  className="mt-(--space-2) text-xs font-semibold no-underline px-(--space-3) py-(--space-1-5) rounded-(--radius-md) transition-colors"
                  style={{ background: 'var(--seed-base)', color: 'var(--surface-primary)' }}
                  data-testid="catalog-connect-networks-link"
                >
                  {t('publications.catalog.connectNetworksLink')}
                </Anchor>
              )}
              {canConnectPortals && (
                <Anchor
                  href={SETTINGS_PORTALS_HREF(agencyId)}
                  className="mt-(--space-2) text-xs font-semibold no-underline px-(--space-3) py-(--space-1-5) rounded-(--radius-md) transition-colors"
                  style={{ background: 'var(--seed-base)', color: 'var(--surface-primary)' }}
                  data-testid="catalog-connect-portals-link"
                >
                  {t('publications.catalog.connectPortalsLink')}
                </Anchor>
              )}
            </div>
          )}
          {showConnectMoreCta && (
            <Anchor
              href={`/app/agencies/${agencyId}/properties`}
              className="mt-(--space-2) text-xs font-semibold no-underline px-(--space-3) py-(--space-1-5) rounded-(--radius-md) transition-colors"
              style={{ background: 'var(--seed-base)', color: 'var(--surface-primary)' }}
              data-testid="catalog-publish-first-link"
            >
              {t('publications.catalog.publishFirstPropertyLink')}
            </Anchor>
          )}
        </div>
      )}

      {!loading && !error && groups.length > 0 && (
        <PublicationSections groups={groups} onSelect={handleSelect} />
      )}

      {selected && (
        <PublicationDetailModal
          entry={selected}
          agencyId={agencyId}
          onClose={handleClose}
          onRemove={handleRemove}
          fbHasPaymentMethod={fbHasPaymentMethod}
          isConnected={connectedPortals.includes(selected.portal === 'instagram' ? 'instagram' : 'facebook')}
          defaultBoostBudget={selected.portal === 'instagram' ? igLeadAdBudget : fbLeadAdBudget}
          defaultBoostDuration={selected.portal === 'instagram' ? igLeadAdDuration : fbLeadAdDuration}
          boostMissing={selected.portal === 'instagram' ? adReadiness.instagram.boost : adReadiness.facebook.boost}
          onSaved={() => {
            handleClose();
            refresh();
          }}
        />
      )}
    </div>
  );
}
